import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import API_BASE_URL from '../../utils/apiConfig';

const ContactSection = () => {
  const { t } = useTranslation();
  const channels = t('contact.channels', { returnObjects: true });
  const formCopy = t('contact.form', { returnObjects: true });

  const fields = (formCopy && formCopy.fields) ? formCopy.fields : {};
  const nameField = fields.name || {};
  const emailField = fields.email || {};
  const requestField = fields.request || {};
  const phoneField = fields.phone || {};
  const descriptionField = fields.description || {};
  const submitLabel = (formCopy && formCopy.submit) || t('contact.form.submit', 'Send Request');
  const submittingLabel = (formCopy && formCopy.submitting) || t('contact.form.submitting', 'Sending...');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    request: '',
    phone: '',
    description: ''
  });
  const [status, setStatus] = useState({ state: 'idle', message: '' });

  const backendBaseUrl = API_BASE_URL;

  const handleChange = (event) => {
    const { name, value } = event.target;
    setStatus((prev) => (prev.state === 'idle' ? prev : { state: 'idle', message: '' }));
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus({ state: 'loading', message: '' });

    try {
      const response = await fetch(`${backendBaseUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          request: formData.request,
          phone: formData.phone,
          description: formData.description,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const fallbackError = (formCopy && formCopy.error) || 'Something went wrong. Please try again later.';
        const errorMessage = data && data.message ? data.message : fallbackError;
        throw new Error(errorMessage);
      }

      const successMessage = (formCopy && formCopy.success) || data.message || '';
      setStatus({ state: 'success', message: successMessage });
      setFormData({
        name: '',
        email: '',
        request: '',
        phone: '',
        description: '',
      });
    } catch (error) {
      const fallbackError = (formCopy && formCopy.error) || 'Something went wrong. Please try again later.';
      setStatus({ state: 'error', message: error.message || fallbackError });
    }
  };

  return (
    <section id="contact" className="section section-padding mutawir-contact">
      <div className="container">
        <div className="row g-5 align-items-stretch">
          <div className="col-xl-5 col-lg-6">
            <div className="mutawir-contact__content">
              <span className="mutawir-tagline">{t('contact.tagline')}</span>
              <h2 className="title">{t('contact.title')}</h2>
              <p className="mutawir-section-description">{t('contact.description')}</p>
              <div className="mutawir-contact__channels">
                {Array.isArray(channels) && channels.map((channel, index) => (
                  <div className="mutawir-card mutawir-card--channel" key={index}>
                    <span className="mutawir-card__title">{channel.label}</span>
                    {channel.href ? (
                      <a href={channel.href} className="mutawir-card__link">
                        {channel.value}
                      </a>
                    ) : (
                      <span className="mutawir-card__text">{channel.value}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="col-xl-5 col-lg-6 offset-xl-1">
            {formCopy && (
              <div className="mutawir-contact__form" role="form">
                <div>
                  <h3 className="mutawir-contact__form-heading">{formCopy.title}</h3>
                  <p className="mutawir-section-description">{formCopy.description}</p>
                </div>
                <form onSubmit={handleSubmit} noValidate aria-busy={status.state === 'loading'}>
                  <div className="mutawir-contact__field">
                    <label htmlFor="contact-name">{nameField.label}</label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={nameField.placeholder}
                      autoComplete="name"
                    />
                  </div>
                  <div className="mutawir-contact__field">
                    <label htmlFor="contact-email">{emailField.label}</label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={emailField.placeholder}
                      autoComplete="email"
                      required
                    />
                  </div>
                  <div className="mutawir-contact__field">
                    <label htmlFor="contact-request">{requestField.label}</label>
                    <input
                      id="contact-request"
                      type="text"
                      name="request"
                      value={formData.request}
                      onChange={handleChange}
                      placeholder={requestField.placeholder}
                      autoComplete="off"
                    />
                  </div>
                  <div className="mutawir-contact__field">
                    <label htmlFor="contact-phone">{phoneField.label}</label>
                    <input
                      id="contact-phone"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder={phoneField.placeholder}
                      autoComplete="tel"
                    />
                  </div>
                  <div className="mutawir-contact__field">
                    <label htmlFor="contact-description">{descriptionField.label}</label>
                    <textarea
                      id="contact-description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder={descriptionField.placeholder}
                      rows={5}
                      required
                    ></textarea>
                  </div>
                  <div className="mutawir-contact__actions">
                    <button
                      type="submit"
                      className="axil-btn btn-fill-primary"
                      disabled={status.state === 'loading'}
                    >
                      {status.state === 'loading' ? submittingLabel : submitLabel}
                    </button>
                    {status.state === 'success' && (
                      <p className="mutawir-contact__success" role="status" aria-live="polite">
                        {status.message}
                      </p>
                    )}
                    {status.state === 'error' && (
                      <p className="mutawir-contact__error" role="status" aria-live="polite">
                        {status.message}
                      </p>
                    )}
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
